<template>
  <Tabs v-model="selectedTab" default-value="email">
    <TabsList class="grid w-full grid-cols-4">
      <TabsTrigger value="email">Email</TabsTrigger>
      <TabsTrigger value="sms">SMS</TabsTrigger>
      <TabsTrigger value="link">Link</TabsTrigger>
      <TabsTrigger value="qrCode">QR Code</TabsTrigger>
    </TabsList>
    <TabsContent value="email">
      <div class="grid grid-cols-4 gap-4 mb-4">
        <div class="col-span-4">
          <div class="space-y-2">
            <LabelComponent>Email</LabelComponent>
            <Input v-model="email" type="email" />
            <span v-if="errors.email !== ''" class="text-sm font-medium text-destructive">
              {{ errors.email }}
            </span>
          </div>
        </div>
      </div>
    </TabsContent>
    <TabsContent value="sms">
      <div class="grid grid-cols-4 gap-4 mb-4">
        <div class="col-span-4">
          <div class="space-y-2">
            <LabelComponent>Phone Number</LabelComponent>
            <Input v-model="mobile" @input="validateMobile" type="text" />
            <span v-if="errors.mobile !== ''" class="text-sm font-medium text-destructive">
              {{ errors.mobile }}
            </span>
          </div>
        </div>
        <div class="col-span-4">
          <div class="flex flex-col space-y-2">
            <LabelComponent>Message</LabelComponent>
            <textarea
              v-model="message"
              class="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span v-if="errors.message !== ''" class="text-sm font-medium text-destructive">
              {{ errors.message }}
            </span>
          </div>
        </div>
      </div>
    </TabsContent>
    <TabsContent value="link">
      <div class="flex gap-2 items-end">
        <div class="space-y-2 w-full">
          <LabelComponent>Link</LabelComponent>
          <Input v-model="link" type="text" readonly />
        </div>
        <Button variant="outline" @click="copyLink">
          <Copy class="h-4 w-4" />
        </Button>
      </div>
    </TabsContent>
    <TabsContent value="qrCode">
      <div class="rounded-lg border border-grey-light-200 p-4 flex justify-center items-center">
        <qrcode-vue :value="link" />
      </div>
    </TabsContent>
  </Tabs>
  <Button v-if="selectedTab === 'email' || selectedTab === 'sms'" @click="onComplete">
    <LayupSpinner v-if="isLoading" :size="4" />
    <span v-else>Send {{ selectedTab === 'email' ? 'email' : 'SMS' }}</span>
  </Button>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BASE_URL, CUSTOMER_UI_URL } from '@/config';
import { NetworkError } from '@/errors';
import type { IOrderShareRequest } from '@/models/orders';
import { shareOrder } from '@/services/emails';
import { useMerchantStore } from '@/stores/merchants';
import { isNumber, isValidEmail, isValidMobile } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { Copy } from 'lucide-vue-next';
import QrcodeVue from 'qrcode.vue';
import { reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  completedOrder: ModelOrderResponse | undefined;
  boostRewards: boolean;
}>();

const toast = useToast();
const merchantStore = useMerchantStore();
const isBoostRewards = ref(props.boostRewards || false);
const selectedTab = ref('email');
const email = ref('');
const mobile = ref('');
const message = ref('');
const link = ref('');
const isLoading = ref(false);

const errors = reactive({
  email: '',
  mobile: '',
  message: '',
});

watch(
  () => props.completedOrder,
  (order) => {
    if (order && order._id && !isBoostRewards.value) {
      link.value = `${CUSTOMER_UI_URL}/order/${order._id}`;
    } else {
      link.value = `${BASE_URL}/shopper/boost/${merchantStore.merchant?._id}`;
    }
  },
  { immediate: true },
);

const resetErrors = () => {
  errors.email = '';
  errors.mobile = '';
  errors.message = '';
};

const sharePaymentPlan = async (payload: IOrderShareRequest) => {
  try {
    isLoading.value = true;
    await shareOrder(payload);
    toast.success(`Sent ${selectedTab.value} successfully!`, { position: 'top' });
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('share-payment-plan', { status: 'network_error', channel: 'merchant', method: 'sharePaymentPlan' });
    } else {
      createException(error);
      addMetric('share-payment-plan', { status: 'error', channel: 'merchant', method: 'sharePaymentPlan' });
    }
    toast.error(error.response.data, { position: 'top' });
  } finally {
    isLoading.value = false;
  }
};

const copyLink = async () => {
  if (!link.value) return;

  try {
    await navigator.clipboard.writeText(link.value);
    toast.success('Copied URL to clipboard!', { position: 'top' });
  } catch (err) {
    console.error('Failed to copy link:', err);
  }
};

const buildPayload = (): IOrderShareRequest => {
  const payload: IOrderShareRequest = {
    data: {
      contact: selectedTab.value === 'email' ? email.value : mobile.value,
      message: message.value,
      orderId: props.completedOrder?._id,
      orderUrl: link.value,
      sendType: selectedTab.value === 'email' ? 'email' : 'sms',
      type: 'orders_send_request',
    },
  };

  return payload;
};

const onComplete = async () => {
  resetErrors();

  if (selectedTab.value === 'email') {
    if (isValidEmail(email.value)) {
      const payload = buildPayload();
      await sharePaymentPlan(payload);
    } else {
      errors.email = 'Please enter a valid email';
    }
  }

  if (selectedTab.value === 'sms') {
    if (isValidMobile(mobile.value)) {
      if (message.value.length <= 100) {
        const payload = buildPayload();
        await sharePaymentPlan(payload);
      } else {
        errors.message = 'Message must be less than 100 characters';
      }
    } else {
      errors.mobile = 'Please enter a valid mobile number';
    }
  }
};

const validateMobile = (event: Event) => {
  const inputValue = isNumber(event);
  mobile.value = inputValue;
};
</script>
